/**
 * Subset of the operations-hub-bff `/hinge-select/*` types this caller uses.
 * Mirrors the BFF DTO shapes verbatim so wire payloads are byte-compatible.
 *
 * Source: operations-hub-bff/src/modules/hinge-select/types.ts (commit f92f43f).
 * Add fields here as new wizard steps need them; do not invent shapes.
 */

export interface ProtoDate {
  year: number;
  month: number;
  day: number;
}

export interface Money {
  currencyCode: string;
  units: string;
  nanos: number;
}

export interface PaginationParams {
  pageSize?: number;
  pageToken?: string;
}

// ----- Knowledge Graph: source tracking ---------------------------------------

export enum Source {
  UNKNOWN = 'SOURCE_UNKNOWN',
  PROVIDER_ROSTER = 'SOURCE_PROVIDER_ROSTER',
  NPPES = 'SOURCE_NPPES',
  MANUAL = 'SOURCE_MANUAL',
  H1 = 'SOURCE_H1',
}

export interface SourceInfo {
  sourceType: Source;
  obtainedAt?: string;
}

// Paths in `fields` must use the proto field names (snake_case) — not the
// camelCase JSON keys used in the message body. `legalName` in the body becomes
// `legal_name` here.
export interface UpdateMask {
  fields: string[];
}

export interface SourceTracking {
  source: SourceInfo;
  updateMask?: UpdateMask;
  externalIdentifier?: string;
}

// ----- Knowledge Graph: enums -------------------------------------------------

export enum CodeSet {
  UNKNOWN = 'CODE_SET_UNKNOWN',
  CPT = 'CODE_SET_CPT',
  HCPCS = 'CODE_SET_HCPCS',
}

export enum BundleType {
  UNKNOWN = 'BUNDLE_TYPE_UNKNOWN',
  VISIT_WITHOUT_ANCHOR = 'BUNDLE_TYPE_VISIT_WITHOUT_ANCHOR',
  VISIT_WITH_ANCHOR = 'BUNDLE_TYPE_VISIT_WITH_ANCHOR',
  ONE_OF_ANCHOR = 'BUNDLE_TYPE_ONE_OF_ANCHOR',
}

export enum ItemBundleRelationshipType {
  UNKNOWN = 'ITEM_BUNDLE_RELATIONSHIP_TYPE_UNKNOWN',
  ANCHOR = 'ITEM_BUNDLE_RELATIONSHIP_TYPE_ANCHOR',
  OPTIONAL = 'ITEM_BUNDLE_RELATIONSHIP_TYPE_OPTIONAL',
}

export enum ProcedureCodeModifierType {
  UNKNOWN = 'PROCEDURE_CODE_MODIFIER_TYPE_UNKNOWN',
  ANY = 'PROCEDURE_CODE_MODIFIER_TYPE_ANY',
  NONE = 'PROCEDURE_CODE_MODIFIER_TYPE_NONE',
  REQUIRED = 'PROCEDURE_CODE_MODIFIER_TYPE_REQUIRED',
}

// ----- Knowledge Graph: entities ---------------------------------------------

export interface Merchant {
  id?: string;
  version?: number;
  name?: string;
  legalName?: string;
  tin?: string;
  providerCustomerAccountId?: string;
  websiteUrl?: string;
  logoUrl?: string;
}

export interface MerchantLocation {
  id?: string;
  version?: number;
  name?: string;
  displayName?: string;
  address?: unknown;
  tin?: string;
  npi2?: string;
  isVirtual?: boolean;
  websiteUrl?: string;
  phoneNumber?: string;
  faxNumber?: string;
  emailAddress?: string;
  isAccessible?: boolean;
  isAcceptingNewPatients?: boolean;
  businessHours?: unknown;
  profileImageSlug?: string;
  googleMapsLink?: string;
  merchantId?: string;
  terminatedAt?: ProtoDate | null;
  terminationReason?: string;
}

export interface ProviderIndividual {
  id?: string;
  version?: number;
  givenName?: string;
  preferredName?: string;
  middleName?: string;
  surname?: string;
  formerSurname?: string;
  honorificPrefix?: string;
  honorificSuffix?: string;
  npi1?: string;
  [k: string]: unknown;
}

export interface AccessPoint {
  id?: string;
  version?: number;
  phoneNumber?: string;
  faxNumber?: string;
  isAcceptingNewPatients?: boolean;
  relationships?: {
    merchantLocation?: MerchantLocation;
    providerIndividual?: ProviderIndividual;
  };
  terminatedAt?: ProtoDate | null;
  terminationReason?: string;
}

export interface Item {
  id?: string;
  version?: number;
  technicalName?: string;
  colloquialName?: string;
  description?: string;
}

export interface ProcedureCode {
  id?: string;
  version?: number;
  codeset: CodeSet;
  code: string;
}

export interface ProcedureCodeToItemRelationship {
  modifierType?: ProcedureCodeModifierType;
  modifierCode?: string;
}

export interface Bundle {
  id?: string;
  version?: number;
  name: string;
  type: BundleType;
}

export interface ItemToBundleRelationship {
  itemId: string;
  relationshipType: ItemBundleRelationshipType;
}

export interface ItemWithBundleRelationship {
  item: Item;
  relationshipType: ItemBundleRelationshipType;
}

// ----- Knowledge Graph: request/response wrappers ----------------------------

export interface ResolveProcedureCodeToItemRequest {
  codeset: CodeSet;
  procedureCode: string;
  modifier?: string;
}
export interface ResolveProcedureCodeToItemResponse {
  item: Item;
}

export interface CreateProcedureCodeRequest {
  procedureCode: ProcedureCode;
  idempotencyKey: string;
  sourceTracking: SourceTracking;
}
export interface CreateProcedureCodeResponse {
  procedureCode: ProcedureCode;
}

export interface CreateItemRequest {
  item: Item;
  idempotencyKey: string;
  sourceTracking: SourceTracking;
}
export interface CreateItemResponse {
  item: Item;
}

// Items in KG can only be browsed via flat pagination — the proto offers no
// server-side name filter (see operations-hub-bff types.ts; KnowledgeGraphService
// has only ListItems with paginationParams). Used by the "Global Fees by Scan
// Type" flow to build a local name → Item.id map.
export interface ListItemsRequest {
  paginationParams: PaginationParams;
}
export interface ListItemsResponse {
  items: Item[];
  nextPageToken: string;
}

export interface GetItemRequest {
  id: string;
}
export interface GetItemResponse {
  item: Item;
}

export interface LinkProcedureCodeToItemRequest {
  procedureCodeId: string;
  itemId: string;
  relationship: ProcedureCodeToItemRelationship;
  idempotencyKey: string;
  sourceTracking: SourceTracking;
}
export type LinkProcedureCodeToItemResponse = Record<string, unknown>;

export interface ListBundlesRequest {
  paginationParams: PaginationParams;
}
export interface ListBundlesResponse {
  bundles: Bundle[];
  nextPageToken: string;
}

export interface CreateBundleRequest {
  bundle: Bundle;
  bundleItems: ItemToBundleRelationship[];
  idempotencyKey: string;
  sourceTracking: SourceTracking;
}
export interface CreateBundleResponse {
  bundle: Bundle;
}

export interface GetBundleAndItemsRequest {
  bundleId: string;
}
export interface GetBundleAndItemsResponse {
  bundle: Bundle;
  items: ItemWithBundleRelationship[];
}

export interface ResolveMerchantByTinRequest {
  tin: string;
  serviceDate?: ProtoDate;
}
export interface ResolveMerchantByTinResponse {
  merchant: Merchant;
}

export interface CreateMerchantRequest {
  merchant: Merchant;
  idempotencyKey: string;
  sourceTracking: SourceTracking;
}
export interface CreateMerchantResponse {
  merchant: Merchant;
}

export interface ResolveMerchantLocationRequest {
  tin: string;
  npi2: string;
  serviceDate?: ProtoDate;
}
export interface ResolveMerchantLocationResponse {
  merchantLocation: MerchantLocation;
}

export interface CreateMerchantLocationRequest {
  merchantLocation: MerchantLocation;
  merchantId?: string;
  idempotencyKey: string;
  sourceTracking: SourceTracking;
}
export interface CreateMerchantLocationResponse {
  merchantLocation: MerchantLocation;
}

export interface LinkMerchantLocationToMerchantRequest {
  merchantLocationId: string;
  merchantId: string;
  idempotencyKey: string;
  sourceTracking: SourceTracking;
}
export type LinkMerchantLocationToMerchantResponse = Record<string, unknown>;

export interface ListMerchantLocationsForMerchantRequest {
  merchantId: string;
}
export interface ListMerchantLocationsForMerchantResponse {
  // Guaranteed by the client wrapper, which normalizes the wire shape. The
  // raw wire response omits this field when the merchant has zero linked
  // locations (proto3 JSON collapses empty repeated fields to absent), so
  // never read merchantLocations off anything other than the wrapper's
  // return value.
  merchantLocations: MerchantLocation[];
}

export interface ResolveProviderIndividualRequest {
  npi1: string;
  serviceDate?: ProtoDate;
}
export interface ResolveProviderIndividualResponse {
  providerIndividual: ProviderIndividual;
}

export interface CreateProviderIndividualRequest {
  providerIndividual: ProviderIndividual;
  idempotencyKey: string;
  sourceTracking: SourceTracking;
}
export interface CreateProviderIndividualResponse {
  providerIndividual: ProviderIndividual;
}

export interface ResolveAccessPointRequest {
  merchantLocationId: string;
  providerIndividualId: string;
  serviceDate?: ProtoDate;
}
export interface ResolveAccessPointResponse {
  accessPoint: AccessPoint;
}

export interface CreateAccessPointRequest {
  accessPoint: AccessPoint;
  merchantLocationId: string;
  providerIndividualId: string;
  idempotencyKey: string;
  sourceTracking: SourceTracking;
}
export interface CreateAccessPointResponse {
  accessPoint: AccessPoint;
}

// ----- Legal Entity: Organization --------------------------------------------

export enum CorpType {
  LLC = 'CORP_TYPE_LLC',
  PARTNERSHIP = 'CORP_TYPE_PARTNERSHIP',
  S_CORP = 'CORP_TYPE_S_CORP',
  B_CORP = 'CORP_TYPE_B_CORP',
  C_CORP = 'CORP_TYPE_C_CORP',
  SOLE_PROPRIETORSHIP = 'CORP_TYPE_SOLE_PROPRIETORSHIP',
  NON_PROFIT = 'CORP_TYPE_NON_PROFIT',
  GOV_ENTITY = 'CORP_TYPE_GOV_ENTITY',
  TRUST_ESTATE = 'CORP_TYPE_TRUST_ESTATE',
  OTHER_BUSINESS_TYPE = 'CORP_TYPE_OTHER_BUSINESS_TYPE',
}

export enum OrganizationState {
  PENDING = 'STATE_PENDING',
  ACTIVE = 'STATE_ACTIVE',
}

export interface OrganizationIdentifier {
  organizationId?: string;
  organizationFein?: string;
}

export interface Organization {
  organizationId?: string;
  legalName?: string;
  dba?: string;
  fein?: string;
  sic?: string;
  naics?: string;
  corpType?: string;
  state?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetOrganizationRequest {
  organizationIdentifier: OrganizationIdentifier;
}

export interface GetOrganizationResponse {
  organization: Organization;
}

export interface CreateOrganizationRequest {
  organization: {
    legalName: string;
    dba?: string;
    fein?: string;
    corpType: CorpType;
    state: OrganizationState;
  };
  idempotencyKey: string;
}

export interface CreateOrganizationResponse {
  organization: {
    organizationId: string;
    legalName: string;
    dba: string;
    corpType: string;
    state: string;
    createdAt: string;
  };
}

// ----- Customer Accounts: ProviderAccount ------------------------------------

export type CustomerState = string;

export interface ProviderAccount {
  providerAccountId?: string;
  organizationId?: string;
  externalId?: string;
  state?: CustomerState;
  createdAt?: string;
  updatedAt?: string;
  organization?: Organization;
}

export interface ProviderAccountIdentifier {
  providerAccountId?: string;
  providerExternalId?: string;
  organizationFein?: string;
}

export interface GetProviderAccountRequest {
  providerAccountIdentifier: ProviderAccountIdentifier;
}
export interface GetProviderAccountResponse {
  providerAccount: ProviderAccount;
}

export interface CreateProviderAccountRequest {
  providerAccount: ProviderAccount;
  idempotencyKey: string;
}
export interface CreateProviderAccountResponse {
  providerAccount: ProviderAccount;
}

// ----- Provider Network: enums -----------------------------------------------

export enum FeeScheduleState {
  UNKNOWN = 'FEE_SCHEDULE_STATE_UNKNOWN',
  DRAFT = 'FEE_SCHEDULE_STATE_DRAFT',
  EXECUTED = 'FEE_SCHEDULE_STATE_EXECUTED',
  ARCHIVED = 'FEE_SCHEDULE_STATE_ARCHIVED',
}

export enum ContractState {
  UNKNOWN = 'CONTRACT_STATE_UNKNOWN',
  DRAFT = 'CONTRACT_STATE_DRAFT',
  EXECUTED = 'CONTRACT_STATE_EXECUTED',
  ARCHIVED = 'CONTRACT_STATE_ARCHIVED',
}

export enum ServiceType {
  UNKNOWN = 'SERVICE_TYPE_UNKNOWN',
  ITEM = 'SERVICE_TYPE_ITEM',
  BUNDLE = 'SERVICE_TYPE_BUNDLE',
}

export enum ServiceProviderType {
  UNKNOWN = 'SERVICE_PROVIDER_TYPE_UNKNOWN',
  MERCHANT_LOCATION = 'SERVICE_PROVIDER_TYPE_MERCHANT_LOCATION',
  ACCESS_POINT = 'SERVICE_PROVIDER_TYPE_ACCESS_POINT',
}

// ----- Provider Network: entities --------------------------------------------

export interface Price {
  amount: Money;
  effectiveStart: ProtoDate;
  effectiveEnd: ProtoDate;
}

export interface ProviderService {
  kgEntityType: ServiceType;
  kgEntityId: string;
  prices: Price[];
}

export interface FeeSchedule {
  id?: string;
  name: string;
  state?: FeeScheduleState;
  services?: ProviderService[];
  effectiveStart: ProtoDate;
  effectiveEnd: ProtoDate;
}

export interface Contract {
  id?: string;
  name: string;
  providerCustomerAccountId: string;
  state: ContractState;
  effectiveStart: ProtoDate;
  effectiveEnd?: ProtoDate;
  version?: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface ContractVersion {
  contractState: Contract;
}

export interface ContractFeeSchedule {
  id: string;
  contractId: string;
  feeScheduleId: string;
}

export interface ContractServiceProvider {
  id?: string;
  contractFeeScheduleId: string;
  knowledgeEntityId: string;
  knowledgeEntityType: ServiceProviderType;
  effectiveStart: ProtoDate;
  effectiveEnd?: ProtoDate;
  feeScheduleId: string;
  version?: number;
}

export interface ContractServiceProviderInput {
  feeScheduleId: string;
  knowledgeEntityId: string;
  knowledgeEntityType: ServiceProviderType;
  effectiveStart: ProtoDate;
  effectiveEnd?: ProtoDate;
}

// ----- Provider Network: request/response wrappers ---------------------------

export interface ListFeeSchedulesRequest {
  contractId?: string;
  feeScheduleStates?: FeeScheduleState[];
  paginationParams: PaginationParams;
}
export interface ListFeeSchedulesResponse {
  feeSchedules: FeeSchedule[];
  nextPageToken: string;
}

export interface CreateFeeScheduleRequest {
  feeSchedule: FeeSchedule;
  idempotencyKey: string;
}
export interface CreateFeeScheduleResponse {
  feeSchedule: FeeSchedule;
}

export interface GetFeeScheduleRequest {
  id: string;
}
export interface GetFeeScheduleResponse {
  feeSchedule: FeeSchedule;
}

export interface UpdateFeeScheduleStateRequest {
  feeScheduleId: string;
  state: FeeScheduleState;
  idempotencyKey: string;
}
export interface UpdateFeeScheduleStateResponse {
  feeSchedule: FeeSchedule;
}

export interface ListContractsForCustomerAccountRequest {
  customerAccountId: string;
}
export interface ListContractsForCustomerAccountResponse {
  contracts: Contract[];
}

export interface CreateContractRequest {
  contract: Contract;
  idempotencyKey: string;
}
export interface CreateContractResponse {
  contract: Contract;
}

export interface GetContractRequest {
  id: string;
}
export interface GetContractResponse {
  contract: Contract;
}

export interface GetContractHistoryRequest {
  contractId: string;
}
export interface GetContractHistoryResponse {
  contractVersions: ContractVersion[];
}

export interface UpdateContractRequest {
  contractId: string;
  name?: string;
  state?: ContractState;
  feeScheduleIds?: string[];
  effectiveStart?: ProtoDate;
  effectiveEnd?: ProtoDate;
  version: number;
  idempotencyKey: string;
}
export interface UpdateContractResponse {
  contract: Contract;
}

export interface AddFeeScheduleToContractRequest {
  contractId: string;
  feeScheduleId: string;
  idempotencyKey: string;
}
export interface AddFeeScheduleToContractResponse {
  contractFeeSchedule: ContractFeeSchedule;
}

export interface AddContractServiceProvidersRequest {
  contractId: string;
  contractServiceProviders: ContractServiceProviderInput[];
  idempotencyKey: string;
}
export interface AddContractServiceProvidersResponse {
  contractServiceProviders: ContractServiceProvider[];
}

export interface GetContractServiceProvidersRequest {
  contractId: string;
}
export interface GetContractServiceProvidersResponse {
  contractServiceProviders: ContractServiceProvider[];
}

export interface GetFeeScheduleForServiceProviderRequest {
  merchantLocationId?: string;
  accessPointId?: string;
  // ISO YYYY-MM-DD string — sent as a flat query param; BFF parses to ProtoDate.
  serviceDate: string;
}
export interface GetFeeScheduleForServiceProviderResponse {
  feeScheduleId: string;
  contractId: string;
}

// ----- Wizard-side discriminated union for resolve-* endpoints ---------------

export type ResolveReason = 'NotFound' | 'Conflict' | 'Terminated';

export type ResolveResult<T> =
  | { ok: true; data: T }
  | { ok: false; reason: ResolveReason };
